import { isSuccessTerminated, TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { useCampaignAgentType, useConversationMetadata, useConversationUuid, useTaskDoc } from '~/hooks'
import { ModalId, useModalController } from '~/hooks/useModal'
import { AgentTaskStatus, agentTaskStatusFromJSON, CommandType } from '~/protos/agents'
import { cm, isCampaignUuid } from '~/utils/utils'
import Planner from '../../code-plan/Planner'
import { TaskStatusDetailModalProps } from '../../modals/TaskStatusDetailsModal'
import { UserPopover } from '../../UserPopover'
import { CampaignBlock } from './CampaignBlock'
import styles from './CampaignTimelineBlock.module.scss'
import { IconCampaignTimelineCompleted, IconCampaignTimelineInProgress, IconCampaignTimelineSubmited } from '~/components/icons'
import CampaignReadOnlyEditor from '../CampaignReadOnlyEditor'

export interface CampaignTimelineBlockProps {
  totalBlock: number
  children: Block[]
  fields: {
    ts: number
    desc: string
    status: string
    index: number
    user_id: number
    user_name: string
  }
}

export function CampaignTimelineBlock({ totalBlock, fields, children }: CampaignTimelineBlockProps) {
  const { ts, desc, status, index, user_id, user_name } = fields
  const taskStatus = agentTaskStatusFromJSON(status)
  const hasTaskDetail = TaskStatuses[taskStatus].detailType !== TaskStatusDetailType.NONE
  const hasPlan = taskStatus == AgentTaskStatus.PLANNING_COMPLETED
  const documentGenerated = taskStatus == AgentTaskStatus.DOCUMENT_GENERATED
  const { openModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)
  const uuid = useConversationUuid()
  const isCampaign = isCampaignUuid(uuid)
  const rawMarkdown = useTaskDoc()
  const command = useCampaignAgentType()

  const onClickDetails = (taskStatus: AgentTaskStatus) => {
    openModal({ title: fields.desc, taskStatus, index })
  }

  const renderUser = (user_id: number, user_name: string) => {
    if (user_id && user_name && user_name !== 'Campaign') {
      return (
        <>
          <span className='ml-[2px] mr-1'>by</span>
          <UserPopover userName={user_name} userId={user_id} />
        </>
      )
    }
    return null
  }

  const convertString = (input: string) => {
    const codeBlockRegex = /```\w*\n([\s\S]*?)```/g
    const matches = [...input.matchAll(codeBlockRegex)]
    
    if (matches.length > 0) {
      return matches[0][1].trim()
    }

    return input
  }

  const isDocCampaignCompleted =
    isCampaign &&
    isSuccessTerminated(taskStatus)
  const renderDesc = (desc: string) => {
    if (isDocCampaignCompleted) {
      return 'Task Completed'
    } else if (desc.includes('Task submitted')) {
      return 'Task submitted'
    } else if (desc.includes('Task in progress')) {
      return 'Task in progress'
    }
    return (
      <>
        {desc} {renderUser(user_id, user_name)}
      </>
    )
  }

  const isLastBlock = () => {
    return index == totalBlock - 1
  }

  const renderCompleteMessage = () => {
    switch (command) {
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
        return (
          <>
            <p className='opacity-60 text-sm'>DataWeave is successfully generated and verified for</p>
            <p className='opacity-60 text-sm'>the provided input and output example/s</p>
          </>
        )
      case CommandType.GENERATE_IMAGE:
        return (
          <>
            <p className='opacity-60 text-sm'>Diagram is successfully generated and verified for</p>
            <p className='opacity-60 text-sm'>the provided input example/s</p>
          </>
        )
      default:
        return null
    }
  }

  return (
    <div className={cm('flex relative', styles.timeline)}>
      <div
        className={cm(
          'timeline-line',
          'absolute top-[9px] left-[11px] bottom-[-15px] w-[2px] text-primary-50 opacity-70',
          'absolute top-[9px] left-[11px] bottom-[-15px] w-[2px] text-primary-50 opacity-70',
          styles.line
        )}
      ></div>
      {isDocCampaignCompleted ? (
        <IconCampaignTimelineSubmited className='absolute top-0'/>
      ) : (
        <>
          {(rawMarkdown === '' && desc.includes('Task in progress')) ? (
            <IconCampaignTimelineInProgress className='absolute top-0 left-[-3px]'/>
          ) : (
            <IconCampaignTimelineSubmited className='absolute top-0'/>
          )}
        </>
      )}
      <div className='timeline-body pl-8 pb-5 max-w-xl mb-4'>
        <div className='desc text-default-500 flex flex-col justify-center'>
          <div className={isLastBlock() ? 'text-[#947BFF]' : 'text-black'}>{renderDesc(desc)}</div>
        </div>
        {isDocCampaignCompleted &&
          <div className='my-2'>
            {renderCompleteMessage()}
          </div>
        }
        {hasPlan ? <Planner readOnly={!isLastBlock()} /> : null}
        {documentGenerated ?
          <CampaignReadOnlyEditor
            code={convertString(rawMarkdown)}
          />
          :
          null
        }
        <div className='children space-y-4'>
          {children.map((block, index) => (
            <CampaignBlock
              key={block.id}
              block={block}
              index={index}
              className={cm(`block-${block.id}`, 'pb-8')}
              childrenClassName='pl-0'
            />
          ))}
        </div>
      </div>
    </div>
  )
}
